import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import AMP_KEYS from '@/configs/amp-track-keys';
import type { DashboardDateFilterState } from '@/features/Dashboard/components/DashboardDateFilter';
import { Dashboard2ColumnType } from '@/features/Dashboard/types/constants';
import type { FeedbackBlockListModalOptions } from '@/features/Feedback/components/FeedbackListModal/types';
import service from '@/services';
import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import { usePopup } from '@/stores/pure/PopupStore';
import { useToast } from '@/stores/pure/ToastStore';
import { GPT_DOMAIN } from '@/types/gptConst';
import { track } from '@/utils/track/track';

import type { FeedbacksSettingT } from '../model/types';
import { buildEffectiveFilterSet } from './use-feedback-gallery-data';

type UseFeedbackGalleryActionsParams = {
  dashboardId: string;
  rowId: string;
  rowIndex: number;
  columnId: string;
  feedbacksSetting: FeedbacksSettingT;
  globalFilter?: StructuredFilter | null;
  globalDateFilter?: DashboardDateFilterState | null;
  onEditModeChange: (state: boolean) => void;
};

const useFeedbackGalleryActions = ({
  dashboardId,
  rowId,
  rowIndex,
  columnId,
  feedbacksSetting,
  globalFilter,
  globalDateFilter,
  onEditModeChange,
}: UseFeedbackGalleryActionsParams) => {
  const queryClient = useQueryClient();
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const setPopup = usePopup();
  const addToast = useToast();

  const invalidateDashboardQuery = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['dashboard2', dashboardId],
    });
  }, [dashboardId, queryClient]);

  const onDone = useCallback(
    async (setting: FeedbacksSettingT) => {
      try {
        await service.dashboard2.changeDashboardFeedbacksSettingByColumn(
          dashboardId,
          rowId,
          columnId,
          setting,
        );
        invalidateDashboardQuery();
        onEditModeChange(false);
        addToast('Feedback gallery updated', 'success');
      } catch {
        addToast('Failed to update feedback gallery.', 'fail');
      }
    },
    [
      addToast,
      columnId,
      dashboardId,
      invalidateDashboardQuery,
      onEditModeChange,
      rowId,
    ],
  );

  const onDelete = useCallback(() => {
    setPopup({
      popupTheme: 'negative',
      type: 'confirm',
      title: 'Remove from dashboard',
      message:
        'Are you sure you want to remove this feedback gallery from the dashboard?',
      confirmText: 'Remove',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await service.dashboard2.deleteColumnInDashboardRow(
            dashboardId,
            rowId,
            columnId,
          );
          invalidateDashboardQuery();
          addToast('Feedback gallery deleted from dashboard', 'success');
        } catch {
          addToast('Error! try again', 'fail');
        }
      },
    });
  }, [
    addToast,
    columnId,
    dashboardId,
    invalidateDashboardQuery,
    rowId,
    setPopup,
  ]);

  const onDuplicate = useCallback(async () => {
    try {
      await service.dashboard2.addDashboardRow(dashboardId, {
        pos: rowIndex,
        columns: [
          {
            type: Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY,
            feedbacks_setting: feedbacksSetting,
          },
        ],
      });
      invalidateDashboardQuery();
      addToast('Feedback gallery duplicated', 'success');
    } catch {
      addToast('Failed to duplicate feedback gallery.', 'fail');
    }
  }, [
    addToast,
    dashboardId,
    feedbacksSetting,
    invalidateDashboardQuery,
    rowIndex,
  ]);

  const onClickAskSyncly = useCallback(() => {
    track(AMP_KEYS.DASHBOARD_CARD_ASK_SYNCLY_CLICK);
    const effectiveFilterSet = buildEffectiveFilterSet(
      feedbacksSetting.filter_set,
      globalFilter,
      globalDateFilter,
    );
    const uniqKey = JSON.stringify({
      type: Dashboard2ColumnType.DASHBOARD_COLUMN_FEEDBACK_GALLERY,
      dashboardId,
      columnId,
      filter: effectiveFilterSet,
    });

    setNewModal<FeedbackBlockListModalOptions>({
      alias: uniqKey,
      type: DETAIL_MODAL_TYPE.FEEDBACK_BLOCK_LIST,
      keys: [uniqKey],
      options: {
        queryKeys: [uniqKey],
        title: `${feedbacksSetting.title} feedback`,
        domain: 'feedback-blocks',
        fetcher: async (
          sortStatus: NotNullableSortStatusT,
          pageParam?: null | string,
        ) => {
          const res = await service.feedback.fetchFeedbackBlockList({
            query: {
              sortby: sortStatus.sortBy,
              order: sortStatus.orderStatus,
              direction: 'next',
              cursor: pageParam || undefined,
            },
            payload: effectiveFilterSet,
          });
          const feedbackList = res.feedbacks || [];
          const lastFeedback = feedbackList[feedbackList.length - 1];

          return {
            count: res.count,
            list: feedbackList,
            paginate_token: lastFeedback?.paginate_token || null,
          };
        },
        socketInfo: {
          domain: GPT_DOMAIN.INSIGHT_EXPLORER,
          insight_explorer: effectiveFilterSet,
        },
      },
    });
  }, [
    columnId,
    dashboardId,
    feedbacksSetting.filter_set,
    feedbacksSetting.title,
    globalDateFilter,
    globalFilter,
    setNewModal,
  ]);

  return {
    onDone,
    onDelete,
    onDuplicate,
    onClickAskSyncly,
  };
};

export default useFeedbackGalleryActions;
